import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import NotificationService from "services/NotificationService";

export const fetchNotifications = createAsyncThunk(
  "notification/fetchNotifications",
  async ({ page = 1, limit = 6 }, { rejectWithValue }) => {
    try {
      const response = await NotificationService.getNotifications({
        page,
        limit,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const countUnreadNotifications = createAsyncThunk(
  "notification/countUnread",
  async (_, { rejectWithValue }) => {
    try {
      const response = await NotificationService.countUnread();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const markNotificationRead = createAsyncThunk(
  "notification/markRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await NotificationService.markAsRead(notificationId);
      return { notificationId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const markAllNotificationsRead = createAsyncThunk(
  "notification/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await NotificationService.markAllAsRead();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

const initialState = {
  list: {
    loading: false,
    data: [],
    total: 0,
    page: 1,
    hasMore: false,
    error: null,
  },
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    addNotification(state, action) {
      // Used for real-time socket push
      state.list.data.unshift(action.payload);
      state.unreadCount += 1;
    },
    clearNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.list.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.list.loading = false;
        const { data, total, page, hasMore } = action.payload;
        if (page === 1) {
          state.list.data = data;
        } else {
          const existingIds = new Set(state.list.data.map((n) => n.id));
          const newItems = data.filter((n) => !existingIds.has(n.id));
          state.list.data = [...state.list.data, ...newItems];
        }
        state.list.total = total;
        state.list.page = page;
        state.list.hasMore = hasMore;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload;
      });

    // Count unread
    builder
      .addCase(countUnreadNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(countUnreadNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.unreadCount = action.payload.count;
      })
      .addCase(countUnreadNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Mark single as read
    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const { notificationId } = action.payload;
      const item = state.list.data.find((n) => n.id === notificationId);
      if (item && !item.isRead) {
        item.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });

    // Mark all as read
    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.list.data.forEach((n) => {
        n.isRead = true;
      });
      state.unreadCount = 0;
    });
  },
});

export const { addNotification, clearNotifications } =
  notificationSlice.actions;
export default notificationSlice.reducer;
